/**
 * The public Contact & Referral form (emergesc.com, 2026-10-06).
 *
 * Posts same-origin to /api/v1/public/website-inquiry. The server makes the
 * BD lead, records the inquiry and emails the visitor and the team (see
 * backend/src/services/website/inquiry.js). Nothing here loads a third-party
 * script, and nothing is sent anywhere until the visitor presses Send.
 *
 * `contact_by_fax_only` is a honeypot checkbox: hidden from people and from
 * assistive technology, never autofilled by a browser (a text field named
 * "website" was), and ticked by bots that tick every box, so only a script
 * ever sends it true. `started_at` / `submitted_at` let the server
 * refuse a form filled faster than a person can.
 */
import React, { useRef, useState } from 'react';

export const INQUIRY_ENDPOINT = '/api/v1/public/website-inquiry';
const PHONE_DISPLAY = '855-711-8626';
const PHONE_TEL = 'tel:+18557118626';

type InquiryType = 'FACILITY_PARTNERSHIP' | 'RESIDENT_REFERRAL' | 'GENERAL_QUESTION';
type Field = 'inquiry_type' | 'first_name' | 'last_name' | 'email' | 'phone' | 'organization' | 'role' | 'message';
type Values = Record<Field, string>;
type Errors = Partial<Record<Field, string>>;

const TYPES: { value: InquiryType; label: string; hint: string }[] = [
  { value: 'FACILITY_PARTNERSHIP', label: 'Facility partnership inquiry', hint: 'Bring on-site specialty care to your community.' },
  { value: 'RESIDENT_REFERRAL', label: 'Resident care referral', hint: 'Refer a resident for dentistry, podiatry, optometry or audiology.' },
  { value: 'GENERAL_QUESTION', label: 'Family, billing, or service question', hint: 'Anything else - we will route it to the right person.' },
];

const LIMITS: Record<Field, number> = {
  inquiry_type: 40, first_name: 100, last_name: 100, email: 254, phone: 30, organization: 200, role: 200, message: 4000,
};

const EMPTY: Values = { inquiry_type: '', first_name: '', last_name: '', email: '', phone: '', organization: '', role: '', message: '' };
const ORDER: Field[] = ['inquiry_type', 'first_name', 'last_name', 'email', 'phone', 'organization', 'role', 'message'];

/** The same rules the server applies, so most mistakes are caught before a round trip. */
export function validateInquiry(v: Values): Errors {
  const e: Errors = {};
  if (!v.inquiry_type) e.inquiry_type = 'Choose what your inquiry is about.';
  if (!v.first_name.trim()) e.first_name = 'Enter your first name.';
  if (!v.last_name.trim()) e.last_name = 'Enter your last name.';
  if (!v.email.trim()) e.email = 'Enter your email address.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) e.email = 'Enter a valid email address.';
  const phone = v.phone.trim();
  if (phone && (!/^[0-9+().\-\s]+((x|ext\.?)\s*\d{1,6})?$/i.test(phone) || (phone.match(/\d/g) || []).length < 7)) {
    e.phone = 'Enter a valid phone number, or leave it blank.';
  }
  if (!v.message.trim()) e.message = 'Tell us how we can help.';
  (Object.keys(LIMITS) as Field[]).forEach((k) => {
    if (!e[k] && v[k].length > LIMITS[k]) e[k] = `Please keep this under ${LIMITS[k].toLocaleString()} characters.`;
  });
  return e;
}

type Status = { kind: 'idle' } | { kind: 'sending' } | { kind: 'sent'; firstName: string } | { kind: 'error'; message: string };

export default function WebsiteInquiryForm() {
  const [values, setValues] = useState<Values>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [honeypot, setHoneypot] = useState(false);
  const startedAt = useRef<number>(Date.now());
  const formRef = useRef<HTMLFormElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const sending = status.kind === 'sending';

  const set = (k: Field) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    setValues((cur) => ({ ...cur, [k]: value }));
    if (errors[k]) setErrors((cur) => { const next = { ...cur }; delete next[k]; return next; });
  };

  const focusFirst = (errs: Errors) => {
    const first = ORDER.find((k) => errs[k]);
    if (!first || !formRef.current) return;
    const el = formRef.current.querySelector<HTMLElement>(first === 'inquiry_type' ? 'input[name="inquiry_type"]' : `[name="${first}"]`);
    el?.focus();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    const errs = validateInquiry(values);
    setErrors(errs);
    if (Object.keys(errs).length) { focusFirst(errs); return; }

    setStatus({ kind: 'sending' });
    try {
      const res = await fetch(INQUIRY_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({
          ...Object.fromEntries(ORDER.map((k) => [k, values[k].trim()])),
          contact_by_fax_only: honeypot,
          started_at: startedAt.current,
          submitted_at: Date.now(),
        }),
      });
      let body: { success?: boolean; message?: string; errors?: Errors } = {};
      try { body = await res.json(); } catch { /* a non-JSON answer is an error below */ }
      if (res.ok && body.success) {
        setStatus({ kind: 'sent', firstName: values.first_name.trim() });
        setTimeout(() => resultRef.current?.focus(), 0);
        return;
      }
      if (res.status === 400 && body.errors) {
        const server: Errors = {};
        ORDER.forEach((k) => { if (body.errors?.[k]) server[k] = String(body.errors[k]); });
        setErrors(server);
        setStatus({ kind: 'idle' });
        focusFirst(server);
        return;
      }
      setStatus({ kind: 'error', message: body.message || `We could not send your request just now. Please try again, or call us at ${PHONE_DISPLAY}.` });
    } catch {
      setStatus({ kind: 'error', message: `We could not reach eMerge just now. Please try again, or call us at ${PHONE_DISPLAY}.` });
    }
  };

  if (status.kind === 'sent') {
    return (
      <div className="inquiry-result inquiry-result--sent" role="status" tabIndex={-1} ref={resultRef}>
        <span className="inquiry-result__icon" aria-hidden="true">&#10003;</span>
        <div>
          <h4>Request received</h4>
          <p>
            Thank you, {status.firstName}. We received your request and will be in touch shortly.
            For anything urgent call <a href={PHONE_TEL}>{PHONE_DISPLAY}</a>.
          </p>
        </div>
      </div>
    );
  }

  const fieldProps = (k: Field) => ({
    id: `inquiry-${k}`,
    name: k,
    value: values[k],
    onChange: set(k),
    maxLength: LIMITS[k],
    disabled: sending,
    'aria-invalid': errors[k] ? true : undefined,
    'aria-describedby': errors[k] ? `inquiry-${k}-error` : undefined,
  });
  const err = (k: Field) => (errors[k] ? <span className="inquiry-field__error" id={`inquiry-${k}-error`}>{errors[k]}</span> : null);
  const isReferral = values.inquiry_type === 'RESIDENT_REFERRAL';

  return (
    <form className="inquiry-form" onSubmit={submit} noValidate ref={formRef} aria-label="Contact and referral form" aria-busy={sending}>
      <fieldset className="inquiry-types" disabled={sending} aria-describedby={errors.inquiry_type ? 'inquiry-inquiry_type-error' : undefined}>
        <legend>What can we help with? <span className="inquiry-required" aria-hidden="true">*</span></legend>
        {TYPES.map((t) => (
          <label key={t.value} className={`inquiry-option inquiry-option--choice${values.inquiry_type === t.value ? ' is-selected' : ''}`}>
            <input
              type="radio"
              name="inquiry_type"
              value={t.value}
              checked={values.inquiry_type === t.value}
              onChange={set('inquiry_type')}
              aria-invalid={errors.inquiry_type ? true : undefined}
            />
            <span className="inquiry-option__check" aria-hidden="true">&#10003;</span>
            <span>
              <span className="inquiry-option__label">{t.label}</span>
              <span className="inquiry-option__hint">{t.hint}</span>
            </span>
          </label>
        ))}
        {err('inquiry_type')}
      </fieldset>

      <div className="inquiry-grid">
        <div className="inquiry-field">
          <label htmlFor="inquiry-first_name">First name <span className="inquiry-required" aria-hidden="true">*</span></label>
          <input type="text" autoComplete="given-name" required {...fieldProps('first_name')} />
          {err('first_name')}
        </div>
        <div className="inquiry-field">
          <label htmlFor="inquiry-last_name">Last name <span className="inquiry-required" aria-hidden="true">*</span></label>
          <input type="text" autoComplete="family-name" required {...fieldProps('last_name')} />
          {err('last_name')}
        </div>
        <div className="inquiry-field">
          <label htmlFor="inquiry-email">Email <span className="inquiry-required" aria-hidden="true">*</span></label>
          <input type="email" autoComplete="email" inputMode="email" required {...fieldProps('email')} />
          {err('email')}
        </div>
        <div className="inquiry-field">
          <label htmlFor="inquiry-phone">Phone <span className="inquiry-optional">(optional)</span></label>
          <input type="tel" autoComplete="tel" inputMode="tel" {...fieldProps('phone')} />
          {err('phone')}
        </div>
        <div className="inquiry-field">
          <label htmlFor="inquiry-organization">Facility or organization <span className="inquiry-optional">(optional)</span></label>
          <input type="text" autoComplete="organization" {...fieldProps('organization')} />
          {err('organization')}
        </div>
        <div className="inquiry-field">
          <label htmlFor="inquiry-role">Your role or title <span className="inquiry-optional">(optional)</span></label>
          <input type="text" autoComplete="organization-title" {...fieldProps('role')} />
          {err('role')}
        </div>
        <div className="inquiry-field inquiry-field--wide">
          <label htmlFor="inquiry-message">How can we help? <span className="inquiry-required" aria-hidden="true">*</span></label>
          <textarea
            rows={5}
            required
            {...fieldProps('message')}
            aria-describedby={['inquiry-message-hint', errors.message ? 'inquiry-message-error' : ''].filter(Boolean).join(' ')}
          />
          <span className="inquiry-field__hint" id="inquiry-message-hint">
            {isReferral
              ? 'Share the basics so we can follow up - there is no need to include detailed medical information here.'
              : 'A few sentences is plenty.'}
          </span>
          {err('message')}
        </div>
      </div>

      {/* Honeypot: off-screen, out of the tab order and hidden from screen readers. */}
      <div className="inquiry-hp" aria-hidden="true">
        <input id="inquiry-fax-only" type="checkbox" name="contact_by_fax_only" tabIndex={-1} autoComplete="off" checked={honeypot} onChange={(e) => setHoneypot(e.target.checked)} />
        <label htmlFor="inquiry-fax-only">Contact me by fax only</label>
      </div>

      {status.kind === 'error' && (
        <div className="inquiry-result inquiry-result--error" role="alert">
          <p>{status.message} <a href={PHONE_TEL}>Call {PHONE_DISPLAY}</a></p>
        </div>
      )}

      <div className="inquiry-actions">
        <button className="btn btn--primary" type="submit" disabled={sending}>
          {sending ? 'Sending…' : 'Send request'}
        </button>
        <span className="inquiry-actions__note">We&rsquo;ll use this information to respond to your inquiry.</span>
      </div>
    </form>
  );
}
